import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardPaste } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IntakeMessageList } from "@/features/intake/IntakeMessageList";
import { IntakeReviewPanel } from "@/features/intake/IntakeReviewPanel";
import { IntakeManualPasteDialog } from "@/features/intake/IntakeManualPasteDialog";
import { IntakePrecisionTab } from "@/features/intake/IntakePrecisionTab";

export default function IntakePage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [tab, setTab] = useState("bandeja");
  const [filters, setFilters] = useState({ canal: "all", estado: "pendiente", q: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["intake-messages", filters],
    enabled: tab === "bandeja",
    queryFn: async () => (await api.get("/intake/messages", {
      params: {
        canal: filters.canal === "all" ? undefined : filters.canal,
        estado: filters.estado === "all" ? undefined : filters.estado,
        q: filters.q || undefined,
        page_size: 100,
      },
    })).data,
  });

  const items = data?.items || [];

  useEffect(() => {
    if (!selectedId && items.length) setSelectedId(items[0].id);
    if (selectedId && items.length && !items.find((m) => m.id === selectedId)) setSelectedId(items[0].id);
  }, [items, selectedId]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tab !== "bandeja") return;
      if (!items.length) return;
      const idx = items.findIndex((m) => m.id === selectedId);
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setSelectedId(items[Math.min(idx + 1, items.length - 1)]?.id);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setSelectedId(items[Math.max(idx - 1, 0)]?.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, selectedId, tab]);

  const refreshList = () => qc.invalidateQueries({ queryKey: ["intake-messages"] });

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        testId="intake-page-header"
        title={t("intake.title")}
        subtitle={t("intake.subtitle")}
        actions={
          <Button data-testid="intake-open-manual-paste" onClick={() => setPasteOpen(true)}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover">
            <ClipboardPaste className="mr-1.5 h-3.5 w-3.5" /> {t("intake.manual.open_button")}
          </Button>
        }
      />
      <div className="border-b border-line bg-white px-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-10 bg-transparent p-0">
            <TabsTrigger data-testid="intake-tab-bandeja" value="bandeja"
              className="rounded-none border-b-2 border-transparent text-[13px] data-[state=active]:border-brand data-[state=active]:bg-transparent">
              {t("intake.tabs.inbox")}
            </TabsTrigger>
            <TabsTrigger data-testid="intake-tab-precision" value="precision"
              className="rounded-none border-b-2 border-transparent text-[13px] data-[state=active]:border-brand data-[state=active]:bg-transparent">
              {t("intake.tabs.precision")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === "bandeja" ? (
        <div className="flex flex-1 overflow-hidden">
          <IntakeMessageList
            items={items} contadores={data?.contadores} filters={filters}
            onFiltersChange={setFilters} selectedId={selectedId} onSelect={setSelectedId}
            loading={isLoading}
          />
          <IntakeReviewPanel messageId={selectedId} onChanged={refreshList} />
        </div>
      ) : (
        <IntakePrecisionTab />
      )}

      <IntakeManualPasteDialog
        open={pasteOpen} onOpenChange={setPasteOpen}
        onCreated={(id) => { refreshList(); if (id) setSelectedId(id); }}
      />
    </div>
  );
}
